// src/components/auth/PendingStates.js
import { useState } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { Mail, Clock, RefreshCw, Loader2 } from 'lucide-react';

export function EmailVerificationRequired({ userEmail }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    setSending(true);
    setError('');
    try {
      await sendEmailVerification(auth.currentUser);
      setSent(true);
      setTimeout(() => setSent(false), 60000); // Reset after 1 minute
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="max-w-lg mx-auto mt-16">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail size={32} className="text-yellow-600" />
        </div>
        
        <h2 className="text-2xl font-bold mb-2">Verify Your Email</h2>
        <p className="text-gray-600 mb-6">
          We've sent a verification email to <strong>{userEmail}</strong>.
          Please check your inbox and click the link to verify your account.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleRefresh}
            className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2"
          >
            <RefreshCw size={18} />
            I've Verified - Refresh Page
          </button>

          <button
            onClick={handleResend}
            disabled={sending || sent}
            className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : sent ? (
              'Email Sent - Check Inbox'
            ) : (
              'Resend Verification Email'
            )}
          </button>
        </div>

        <p className="text-sm text-gray-500 mt-6">
          Don't see the email? Check your spam folder.
        </p>
      </div>
    </div>
  );
}

export function ApprovalPending() {
  return (
    <div className="max-w-lg mx-auto mt-16">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock size={32} className="text-blue-600" />
        </div>
        
        <h2 className="text-2xl font-bold mb-2">Account Pending Approval</h2>
        <p className="text-gray-600 mb-6">
          Thank you for verifying your email! Your wholesale account is now under review.
        </p>

        <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
          <h3 className="font-semibold mb-3">What happens next?</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              Email verified
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">⏳</span>
              Our team reviews your application
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">○</span>
              You'll receive an email when approved
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">○</span>
              Start placing wholesale orders!
            </li>
          </ul>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Tip:</strong> Upload your W9 form in your profile to speed up the approval process.
          </p>
        </div>

        <p className="text-sm text-gray-500 mt-6">
          Questions? Contact us at <a href="mailto:wholesaler@4skin.biz" className="text-black underline">wholesaler@4skin.biz</a>
        </p>
      </div>
    </div>
  );
}
